import { useState, useEffect, useCallback, useMemo } from 'react';
import { scheduleService, type ScheduleResponse } from '../services/schedule';
import { expandScheduleOccurrences } from '../utils/expandScheduleOccurrences';

export type { ScheduleOccurrence } from '../utils/scheduleOccurrenceTypes';

export function useSchedule(options?: {
  studentId?: string;
  academyId?: string;
}) {
  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [occurrences, setOccurrences] = useState<ReturnType<typeof expandScheduleOccurrences>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const schedule = await scheduleService.getMySchedule(options);
      setData(schedule);
      setOccurrences(expandScheduleOccurrences(schedule));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedule');
      setData(null);
      setOccurrences([]);
    } finally {
      setLoading(false);
    }
  }, [options?.studentId, options?.academyId]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const makeupOccurrenceSet = useMemo(() => {
    const arr = data?.makeupOccurrences ?? [];
    return new Set(
      arr.map((o) => {
        const id = 'bookingId' in o ? o.bookingId : o.makeupSessionReservationId;
        return `${id}:${o.date}`;
      })
    );
  }, [data?.makeupOccurrences]);

  return {
    data,
    occurrences,
    makeupOccurrenceSet,
    loading,
    error,
    refetch: fetchSchedule,
  };
}
